import includes from 'lodash/includes';
import React, { useContext } from 'react';
import {
    DECORATIVE_IMAGE_PLACEMENT_MAP,
    EVENT_TIME_OPTIONS_TEMPLATE,
    NON_EVENT_BUCKET_SPACING_CLASSES,
    ORGANIZER_FOLLOW_TEMPLATE,
} from '../../../../constants';
import {
    Bucket,
    BucketsContent,
    FeatureFlags,
    ThingsToDoLocationData,
} from '../../../../types';
import { BrandBucket } from '../../components/BrandBucket';
import { BrowseDataContext, BrowseDataContextData } from '../../context';
import { BrowseBucketContainer } from '../BrowseBucket';
import { PointOfInterestShelfContainer } from '../PointOfInterestShelfContainer';
import { ThingsToDoShelfContainer } from '../ThingsToDoShelfContainer';
import './mainContent.scss';
import {
    getBucketClasses,
    getDecorativeImage,
    prepareThingsToDoShelfData,
} from './utils';

const SMALL_CITY_KEY = 'small_city_fallback';

const _getBuckets = ({
    buckets,
    fetchNextPage,
    hasNextPage,
    removeEvent,
    decorativeImages,
}: BrowseDataContextData & { decorativeImages: { [key: string]: string } }) => {
    /* Creates an array to keep the used decorativeImages and prevent duplicates. */
    const usedDecorativeImages: string[] = [];
    // Filter bucket='cnl' from this flow , since it will be rendered in Neighbourhood component
    return buckets
        .filter((bucket) => bucket.keyMin !== 'cnl')
        .map((bucket: Bucket, index: number) => {
            const { template = '', key = '', name = '' } = bucket;

            /* DECORATIVE IMAGE LOGIC */
            const { decorativeImage, decorationPlacement } = getDecorativeImage(
                decorativeImages,
                key,
                usedDecorativeImages,
            );
            /* END DECORATIVE IMAGE LOGIC */

            /* CONTAINER CLASS LOGIC */
            const { bucketWrapperClasses, containerClasses } = getBucketClasses(
                key,
                decorativeImage || '',
                template,
            );
            /* END CONTAINER CLASS LOGIC */

            const shouldFetchMoreBuckets =
                hasNextPage && index === buckets.length - 1;

            return (
                <div key={name} className={`${key}--bucket-wrapper`}>
                    <BrowseBucketContainer
                        containerClasses={containerClasses}
                        bucketWrapperClasses={bucketWrapperClasses}
                        decorationPlacement={decorationPlacement}
                        decorativeImage={decorativeImage}
                        bucketIndex={index}
                        bucketKey={key}
                        onRemoveEvent={removeEvent}
                        onEnter={
                            shouldFetchMoreBuckets ? fetchNextPage : undefined
                        }
                        {...bucket}
                    />
                </div>
            );
        });
};

const insertBrandBucket = (
    bucketSections: JSX.Element[],
    middleImage?: string,
    middleSmallImage?: string,
) => {
    if (!middleImage) {
        return bucketSections;
    }

    //We want to insert the large image midway down the page. But it shouldn't
    //be immediately next to one of our decorative images
    const midIndex = Math.floor(bucketSections.length / 2);
    const insertIndex = includes(
        Object.keys(DECORATIVE_IMAGE_PLACEMENT_MAP),
        `${midIndex}`,
    )
        ? midIndex + 1
        : midIndex;

    const middleImageComponent = (
        <div key="brand-image">
            <BrandBucket
                middleImage={middleImage}
                middleSmallImage={middleSmallImage}
            />
        </div>
    );

    return [
        ...bucketSections.slice(0, insertIndex),
        middleImageComponent,
        ...bucketSections.slice(insertIndex),
    ];
};

const insertTTDBucket = (
    buckets: BucketsContent,
    bucketsElements: JSX.Element[],
    locationData: ThingsToDoLocationData,
) => {
    const { header, tags } = prepareThingsToDoShelfData(buckets, locationData);
    return [
        ...bucketsElements,
        <ThingsToDoShelfContainer
            key="things_to_do_shelf"
            header={header}
            tags={tags}
        />,
    ];
};

const organizerFollowBucket = {
    key: 'follow_organizer',
    bucketKey: 'follow_organizer',
    name: 'Featured Organizers',
    template: ORGANIZER_FOLLOW_TEMPLATE,
    type: 'follow_organizer',
};

const insertFollowOrganizerBucket = (
    buckets: JSX.Element[],
    isLargeCity: boolean,
) => {
    const { key, name } = organizerFollowBucket;
    const insertIndex = isLargeCity ? 3 : 2;
    const FollowOrganizer = (
        <div key={name} className={`${key}--bucket-wrapper`}>
            <BrowseBucketContainer
                containerClasses={NON_EVENT_BUCKET_SPACING_CLASSES}
                {...organizerFollowBucket}
            />
        </div>
    );

    return [
        ...buckets.slice(0, insertIndex),
        FollowOrganizer,
        ...buckets.slice(insertIndex),
    ];
};

const eventTimeOptionsBucket = {
    key: 'event_time_options',
    bucketKey: 'event_time_options',
    name: 'Event time options',
    template: EVENT_TIME_OPTIONS_TEMPLATE,
    type: 'event_time_options',
};

const insertEventTimeOptionsBucket = (buckets: JSX.Element[]) => {
    const { key, name } = eventTimeOptionsBucket;
    const EventTimeOptions = (
        <div key={name} className={`${key}--bucket-wrapper`}>
            <BrowseBucketContainer
                containerClasses={NON_EVENT_BUCKET_SPACING_CLASSES}
                {...eventTimeOptionsBucket}
            />
        </div>
    );
    return [...buckets, EventTimeOptions];
};

const insertPOIBucket = (buckets: JSX.Element[]) => {
    return [
        ...buckets,
        <PointOfInterestShelfContainer key="point_of_interest_shelf" />,
    ];
};

interface MainContentProps {
    middleImage: string;
    middleSmallImage: string;
    decorationTopLeftUrl: string;
    decorationTopRightUrl: string;
    decorationBottomLeftUrl: string;
    decorationBottomRightUrl: string;
    featureFlags?: FeatureFlags;
    locationData: ThingsToDoLocationData;
}

export const MainContent = (props: MainContentProps) => {
    const {
        buckets,
        fetchNextPage,
        hasNextPage,
        removeEvent,
    }: BrowseDataContextData = useContext(BrowseDataContext);

    const {
        middleImage,
        middleSmallImage,
        decorationTopLeftUrl,
        decorationBottomLeftUrl,
        decorationBottomRightUrl,
        locationData,
    } = props;

    const decorativeImages = {
        decorationTopLeftUrl,
        decorationBottomLeftUrl,
        decorationBottomRightUrl,
    };

    /* Insert Follow Organizer Bucket into the response. Once
     backend does this for us will no longer be necessary */
    const [firstBucket] = buckets || [];
    const isLargeCity = firstBucket && firstBucket.key !== SMALL_CITY_KEY;

    const bucketSections = _getBuckets({
        buckets,
        fetchNextPage,
        hasNextPage,
        removeEvent,
        decorativeImages,
    });

    const bucketSectionsWithOrganizer = insertFollowOrganizerBucket(
        bucketSections,
        isLargeCity,
    );

    const bucketSectionsWithBrandAndOrganizer = insertBrandBucket(
        bucketSectionsWithOrganizer,
        middleImage,
        middleSmallImage,
    );

    const bucketSectionsWithEventTimeOptions = insertEventTimeOptionsBucket(
        bucketSectionsWithBrandAndOrganizer,
    );

    const bucketSectionsWithTTD = insertTTDBucket(
        buckets || [],
        bucketSectionsWithEventTimeOptions,
        locationData,
    );

    if (props.featureFlags?.enable_poi_near_location_city_browse) {
        return (
            <div className="main-content">
                {insertPOIBucket(bucketSectionsWithTTD)}
            </div>
        );
    } else {
        return <div className="main-content">{bucketSectionsWithTTD}</div>;
    }
};
